import { FleetKind, FleetRole } from 'src/app/models/drivers-fleet';

/**
 * Escenarios del mock de flotas (estados de DDEV-6). Se eligen con
 * `?fleetMock=<escenario>` en cualquier URL o desde la consola:
 *
 *   localStorage.setItem('drivers-fleet-mock', JSON.stringify({ scenario: 'rechazo', kind: 'vtc_fleet', role: 'owner' }))
 *
 * `?fleetMock=off` quita el escenario (y con él el rol de flota de `/me`).
 * `?fleetMock=reset` borra los datos simulados y vuelve a sembrar.
 */
export type FleetMockScenario = 'alta' | 'panel-vacio' | 'con-altas' | 'rechazo' | 'aprobada';
export const FLEET_MOCK_SCENARIOS: readonly FleetMockScenario[] = [
  'alta',
  'panel-vacio',
  'con-altas',
  'rechazo',
  'aprobada',
];

export type FleetMockRoleKey = 'owner' | 'manager' | 'accountant';
export const FLEET_MOCK_ROLE: Record<FleetMockRoleKey, FleetRole> = {
  owner: 'fleet_owner',
  manager: 'fleet_manager',
  accountant: 'fleet_accountant',
};

export interface FleetMockSettings {
  /** `null`: sin escenario; `/me` no se toca y la flota del titular no existe. */
  scenario: FleetMockScenario | null;
  /** Tipo de la flota del titular: `taxi` → patrón, `vtc` → flota VTC, `emisora` → emisora. */
  kind: FleetKind;
  role: FleetMockRoleKey;
}

export const FLEET_MOCK_SETTINGS_KEY = 'drivers-fleet-mock';
export const FLEET_MOCK_STATE_KEY = 'drivers-fleet-mock-state';

/** Latencia simulada (ms). */
export const FLEET_MOCK_LATENCY: readonly [number, number] = [300, 700];

/** Pantallas que da cada rol de flota en `pages[]` de `/me` (contrato, «Ámbito y sesión»). */
export const FLEET_ROLE_PAGES: Record<FleetRole, string[]> = {
  fleet_owner: [
    'fleet_overview',
    'fleet_drivers',
    'fleet_vehicles',
    'fleet_shifts',
    'fleet_documents',
    'fleet_settlements',
    'fleet_performance',
    'fleet_reports',
    'fleet_team',
    'fleet_settings',
  ],
  // Mockup 28: el gestor lleva la operación (sin Ajustes ni dinero); el
  // contable, dinero y papeles.
  fleet_manager: ['fleet_overview', 'fleet_drivers', 'fleet_vehicles', 'fleet_shifts', 'fleet_documents', 'fleet_performance'],
  fleet_accountant: ['fleet_overview', 'fleet_documents', 'fleet_settlements', 'fleet_reports'],
};

/** Pantallas solo de emisora, y las que una emisora no tiene (sus socios cobran directamente). */
const DISPATCH_CENTER_PAGES = ['fleet_stands', 'fleet_zones', 'fleet_dispatch_policy'];
const NOT_IN_DISPATCH_CENTER = ['fleet_shifts', 'fleet_settlements'];

/** Pantallas (`pages[]` de `/me`) de un rol según el tipo de flota. */
export function fleetRolePages(role: FleetRole, kind: FleetKind): string[] {
  const base = FLEET_ROLE_PAGES[role];
  if (kind !== 'dispatch_center') return base;
  const extra = role === 'fleet_owner' ? DISPATCH_CENTER_PAGES : role === 'fleet_manager' ? ['fleet_stands', 'fleet_zones'] : [];
  return [...base.filter((page) => !NOT_IN_DISPATCH_CENTER.includes(page)), ...extra];
}

const DEFAULT_SETTINGS: FleetMockSettings = { scenario: null, kind: 'owner_operator', role: 'owner' };

/** Palabra de `?fleetMock=` → tipo de flota. */
export const FLEET_MOCK_KIND_TOKENS: Record<string, FleetKind> = {
  taxi: 'owner_operator',
  patron: 'owner_operator',
  vtc: 'vtc_fleet',
  emisora: 'dispatch_center',
};

export function readFleetMockSettings(): FleetMockSettings {
  try {
    const raw = localStorage.getItem(FLEET_MOCK_SETTINGS_KEY);
    if (!raw) {
      return { ...DEFAULT_SETTINGS };
    }
    const parsed = JSON.parse(raw) as Partial<FleetMockSettings> & { mode?: string };
    return {
      scenario: FLEET_MOCK_SCENARIOS.includes(parsed.scenario as FleetMockScenario)
        ? (parsed.scenario as FleetMockScenario)
        : null,
      kind: ['dispatch_center', 'owner_operator', 'vtc_fleet'].includes(parsed.kind as string)
        ? (parsed.kind as FleetKind)
        : parsed.mode === 'vtc'
          ? 'vtc_fleet'
          : 'owner_operator',
      role: parsed.role && parsed.role in FLEET_MOCK_ROLE ? parsed.role : 'owner',
    };
  } catch {
    return { ...DEFAULT_SETTINGS };
  }
}

export function writeFleetMockSettings(settings: FleetMockSettings): void {
  localStorage.setItem(FLEET_MOCK_SETTINGS_KEY, JSON.stringify(settings));
}

/**
 * Aplica `?fleetMock=…` de la URL actual: escenario, tipo (`taxi`/`patron`,
 * `vtc`, `emisora`), rol, `off` o `reset`, separados por comas
 * (`?fleetMock=aprobada,emisora,manager`).
 * Devuelve `true` si hay que volver a sembrar los datos.
 */
export function applyFleetMockQueryParam(search: string = window.location.search): boolean {
  const value = new URLSearchParams(search).get('fleetMock');
  if (!value) {
    return false;
  }
  const settings = readFleetMockSettings();
  let reseed = false;
  for (const token of value.split(',').map((t) => t.trim().toLowerCase())) {
    if (token === 'off') {
      settings.scenario = null;
      reseed = true;
    } else if (token === 'reset') {
      reseed = true;
    } else if (token in FLEET_MOCK_KIND_TOKENS) {
      const kind = FLEET_MOCK_KIND_TOKENS[token];
      reseed = reseed || settings.kind !== kind;
      settings.kind = kind;
    } else if (token in FLEET_MOCK_ROLE) {
      settings.role = token as FleetMockRoleKey;
    } else if (FLEET_MOCK_SCENARIOS.includes(token as FleetMockScenario)) {
      reseed = reseed || settings.scenario !== token;
      settings.scenario = token as FleetMockScenario;
    }
  }
  writeFleetMockSettings(settings);
  return reseed;
}
